import { ShowErrors } from 'app/components/show-errors';
import { Column } from 'app/design/primitives/box';
import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { SearchInput } from 'app/design/primitives/input/search';
import { Modal } from 'app/design/primitives/modal';
import { Text } from 'app/design/primitives/text';
import {
  useLeaveGroupConversation,
  useOneChannel,
  useUpdateConversationMemberRole,
  SUPPORT_CHANNEL_URL,
} from 'app/network/chat/channel';
import { useReportUser, useUser } from 'app/network/user';
import { navigateToUserProfile } from 'app/screens/billboard/navigator-helper';
import { navigateToChat } from 'app/screens/chat/navigator-helper';
import { ChatConversationMemberRole, CustomChannelType, type ChatUser } from 'app/types/chat';
import type { NavigationScreen } from 'app/types/navigation';
import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList } from 'react-native';
import { useNavigationOptions } from 'app/hooks/navigation';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconAlertTriangle, IconCircleX, IconCrown, IconUser, IconUserCog } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { HeaderRight } from './header-right';
import { MemberRow } from './member-row';
import type { TFunction } from 'i18next';

const theme = createDSTheme();

type ConfirmAction = 'makeAdmin' | 'makeRegular' | 'remove' | 'report';

const MemberConfirmationModal = ({
  action,
  confirmAction,
  isPending,
  memberName,
  onClose,
  onConfirm,
  t,
}: {
  action: ConfirmAction;
  confirmAction: ConfirmAction | null;
  isPending: boolean;
  memberName: string | undefined;
  onClose: () => void;
  onConfirm: () => void;
  t: TFunction;
}) => (
  <Modal transparent open={confirmAction === action} onClose={onClose}>
    <Modal.Card>
      <Modal.Title allowClose onClose={onClose}>
        {t(`memberMenu.${action}Modal.title`)}
      </Modal.Title>
      <Text textAlign="center">{t(`memberMenu.${action}Modal.body`, { name: memberName })}</Text>
      <BottomSheet.ActionButton onPress={onClose} variant="secondary">
        {t('memberMenu.cancel')}
      </BottomSheet.ActionButton>
      <BottomSheet.ActionButton onPress={onConfirm} variant="primary" style={{ opacity: isPending ? 0.5 : 1 }}>
        {t('memberMenu.confirm')}
      </BottomSheet.ActionButton>
    </Modal.Card>
  </Modal>
);

export const ChatMembersScreenComponent: NavigationScreen<'chat-members'> = ({ navigation, route }) => {
  const url = route.params.url;
  const { t } = useTranslation('one-channel');
  const { data: user } = useUser();

  useLayoutEffect(() => {
    navigation.setOptions({ title: t('membersTitle') });
  }, [navigation, t]);

  const { data: channel } = useOneChannel(url);
  const [search, setSearch] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<ChatUser | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

  const isAdmin =
    channel?.custom_type === CustomChannelType.Group && channel.current_user_role === ChatConversationMemberRole.Admin;

  useNavigationOptions(navigation, {
    headerRight: isAdmin ? () => <HeaderRight navigation={navigation} route={route} /> : undefined,
  });

  const { mutate: updateRole, isPending: isUpdatingRole } = useUpdateConversationMemberRole();
  const { mutate: removeMember, isPending: isRemovingMember, error: removeMemberError } = useLeaveGroupConversation();
  const { mutate: reportUser, isPending: isReportingUser } = useReportUser();

  const hasSearch = Boolean(search.trim());
  const lowerSearch = search.toLowerCase();
  const filteredMembers =
    channel?.members.filter(
      (member) =>
        !hasSearch ||
        member.nickname.toLowerCase().includes(lowerSearch) ||
        member.firstname.toLowerCase().includes(lowerSearch),
    ) ?? [];

  const pendingActionRef = useRef<ConfirmAction | null>(null);

  const closeMenu = () => setMenuOpen(false);
  const closeConfirm = () => {
    setConfirmAction(null);
    setSelectedMember(null);
  };

  const handleMenuAction = (action: ConfirmAction) => {
    pendingActionRef.current = action;
    closeMenu();
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
    if (pendingActionRef.current) {
      const action = pendingActionRef.current;
      pendingActionRef.current = null;
      setTimeout(() => setConfirmAction(action), 500);
    }
  };

  const isPending = isUpdatingRole || isRemovingMember || isReportingUser;

  const handleConfirm = () => {
    if (!selectedMember || isPending) return;
    if (confirmAction === 'makeAdmin') {
      updateRole(
        { url, userId: selectedMember.user_id, role: ChatConversationMemberRole.Admin },
        { onSuccess: closeConfirm },
      );
    } else if (confirmAction === 'makeRegular') {
      updateRole(
        { url, userId: selectedMember.user_id, role: ChatConversationMemberRole.Regular },
        { onSuccess: closeConfirm },
      );
    } else if (confirmAction === 'remove') {
      removeMember({ url, userId: selectedMember.user_id }, { onSuccess: closeConfirm });
    } else if (confirmAction === 'report') {
      reportUser(selectedMember.user_id, {
        onSuccess: () => {
          closeConfirm();
          navigateToChat(navigation, SUPPORT_CHANNEL_URL, {
            i18nKey: 'reportUserPrompt',
            ns: 'neighbour-profile',
          });
        },
      });
    }
  };

  return (
    <Column flex={1} style={{ backgroundColor: 'white' }}>
      <ShowErrors error={removeMemberError} />
      <Column
        style={{
          paddingHorizontal: theme.spacing['spacing.general.x4'],
          paddingTop: theme.spacing['spacing.general.x3'],
          gap: theme.spacing['spacing.general.x2'],
        }}
      >
        <SearchInput onSubmit={setSearch} searchAsYouType submitOnEmptyChange />
        {channel ? (
          <Text fontWeight="700" style={{ paddingVertical: theme.spacing['spacing.general.x1'] }}>
            {t('membersCount', { count: channel.member_count })}
          </Text>
        ) : null}
      </Column>
      <FlatList
        data={filteredMembers}
        keyExtractor={(item) => item.user_id}
        renderItem={({ item }) => (
          <MemberRow
            member={item}
            onPress={
              item.user_id !== user?.id
                ? () => {
                    setSelectedMember(item);
                    setMenuOpen(true);
                  }
                : undefined
            }
            isAdmin={item.role === ChatConversationMemberRole.Admin}
          />
        )}
        contentContainerStyle={{ paddingHorizontal: 16, paddingTop: 8, paddingBottom: 24, gap: 8 }}
        showsVerticalScrollIndicator={false}
      />

      {/* Member action bottom sheet */}
      <BottomSheet open={menuOpen} onClose={handleMenuClose}>
        {isAdmin && selectedMember?.role !== ChatConversationMemberRole.Admin ? (
          <BottomSheet.Action
            onPress={() => handleMenuAction('makeAdmin')}
            prefix={<IconCrown strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.secondary']} />}
          >
            <Text variant="bodyBase">{t('memberMenu.makeAdmin')}</Text>
          </BottomSheet.Action>
        ) : null}
        {isAdmin && selectedMember?.role !== ChatConversationMemberRole.Regular ? (
          <BottomSheet.Action
            onPress={() => handleMenuAction('makeRegular')}
            prefix={<IconUserCog strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.secondary']} />}
          >
            <Text variant="bodyBase">{t('memberMenu.makeRegular')}</Text>
          </BottomSheet.Action>
        ) : null}
        <BottomSheet.Action
          onPress={() => {
            closeMenu();
            if (selectedMember) navigateToUserProfile(navigation, selectedMember.user_id);
          }}
          prefix={<IconUser strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.secondary']} />}
        >
          <Text variant="bodyBase">{t('memberMenu.seeProfile')}</Text>
        </BottomSheet.Action>
        {isAdmin ? (
          <BottomSheet.Action
            onPress={() => handleMenuAction('remove')}
            prefix={<IconCircleX strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['surface.error.default']} />}
          >
            <Text variant="bodyBase" color="surface.error.default">
              {t('memberMenu.remove')}
            </Text>
          </BottomSheet.Action>
        ) : null}
        <BottomSheet.Action
          onPress={() => handleMenuAction('report')}
          prefix={<IconAlertTriangle strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['surface.error.default']} />}
        >
          <Text variant="bodyBase" color="surface.error.default">
            {t('memberMenu.report')}
          </Text>
        </BottomSheet.Action>
      </BottomSheet>

      {(['makeAdmin', 'makeRegular', 'remove', 'report'] as const).map((action) => (
        <MemberConfirmationModal
          key={action}
          {...{ action, confirmAction, isPending, t }}
          memberName={selectedMember?.nickname}
          onClose={closeConfirm}
          onConfirm={handleConfirm}
        />
      ))}
    </Column>
  );
};
